import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { EnvService } from './env.service';
import { BillingUsageService } from './views/my-space/billing-usage/billing-usage.service';
import { TenantBrandingService } from './shared/services/tenant-branding/tenant-branding.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private envService = inject(EnvService);
  private injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith('assets/')) {
      return next.handle(req);
    }
    return from(this.authService.getValidToken()).pipe(
      switchMap((tokenData) => {
        const token = tokenData ? tokenData.token : null;
        const decodedToken = tokenData ? tokenData.decodedToken : null;
        const appName = this.envService.appName;
        const linkPrefix = this.envService.linkPrefix;
        const apiUrl = this.envService.apiUrl;
        const bucket = this.envService.linkPrefix?.replace('https://', '').split('/')[1] || '';
        const config = JSON.parse(localStorage.getItem('configurations') || '{}');
        const defaultProjectId = config?.defaultProject?._id || null;
        const notificationToken = localStorage.getItem('notificationToken') || '';

        // Tenant resolution: URL path resolution (/shared/:tenantId/:shareToken) > active_workspace > appName fallback
        let urlTenantId = '';
        const pathSegments = window.location.pathname.split('/');
        if (pathSegments[1] === 'shared' && pathSegments[2] && pathSegments[3]) {
          urlTenantId = pathSegments[2];
        }

        // Fallback to URL query parameter or cached guest session for video-call guest flow
        let urlParamsTenantId = '';
        if (window.location.search) {
          const urlParams = new URLSearchParams(window.location.search);
          urlParamsTenantId = urlParams.get('tenantId') || '';
        }

        let sessionStorageTenantId = '';
        if (pathSegments[1] === 'video-call' && pathSegments[2]) {
          const callId = pathSegments[2];
          const cachedGuest = sessionStorage.getItem(`skz_guest_${callId}`);
          if (cachedGuest) {
            try {
              sessionStorageTenantId = JSON.parse(cachedGuest).tenantId || '';
            } catch (e) {}
          }
        }

        // Get the resolved tenant ID from TenantBrandingService config if available
        const tenantBrandingService = this.injector.get(TenantBrandingService);
        const resolvedTenantId = tenantBrandingService.tenantConfig()?.tenantId || '';

        const activeWorkspace = localStorage.getItem('active_workspace');
        const tenantId = urlTenantId || urlParamsTenantId || sessionStorageTenantId || resolvedTenantId || activeWorkspace || appName;

        let url = req.url;
        const regex = /(?:\.([^.]+))?$/;
        const extension = regex.exec(url);

        // Remove trailing slash if it's not an asset
        if (url.endsWith('/') && !url.startsWith('assets/')) {
          url = url.slice(0, -1);
        }

        if (token && !req.url.includes('userid')) {
          const userId = decodedToken?.user_id || decodedToken?.sub;
          if (userId) {
            url = url.includes('?') ? url + '&userid=' + userId : url + '?userid=' + userId;
          }
        }

        if (extension[1] != 'json' && !req.url.startsWith('assets/')) {
          url = url.includes('?') ? url + '&app_name=' + appName : url + '?app_name=' + appName;
        }

        if (req.url.includes('googleapis')) return next.handle(req);

        function cloneReq() {
          let headers = req.headers;
          if (!url.includes('googleapis')) {
            if (!(req.body instanceof FormData) && headers && !headers.has('Content-Type')) {
              headers = headers.set('Content-Type', 'application/json');
            }
            const language = localStorage.getItem('language');

            if (language) headers = headers.set('language', language);
            if (appName) headers = headers.set('x-app-name', appName);
            if (tenantId) headers = headers.set('x-tenant-id', tenantId);
            if (linkPrefix) headers = headers.set('x-link-prefix', linkPrefix);
            if (bucket) headers = headers.set('x-bucket-name', bucket);
            if (apiUrl) headers = headers.set('x-api-url', apiUrl);
            if (token) headers = headers.set('Authorization', 'Bearer ' + token);
            if (defaultProjectId) headers = headers.set('x-project-id', defaultProjectId);
            if (notificationToken) headers = headers.set('x-notification-token', notificationToken);
          }
          return req.clone({
            url,
            method: req.method,
            headers,
          });
        }

        req = cloneReq();

        if (req.method === 'POST') {
          if (req.body instanceof FormData) {
            if (notificationToken) {
              req.body.append('notificationToken', notificationToken);
            }
          } else {
            req = req.clone({ body: { ...req.body, notificationToken } });
          }
        }
        return next.handle(req).pipe(
          tap((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
              const body = event.body;
              if (body) {
                const isNotEnoughCredits = body.msg === 'notEnoughCredits' || body.message === 'notEnoughCredits' || body.messageCode === 'notEnoughCredits';

                if (isNotEnoughCredits) {
                  try {
                    const billingService = this.injector.get(BillingUsageService);
                    billingService.creditAlertTriggered.set(true);
                  } catch (e) {
                    console.error('Failed to set creditAlertTriggered from interceptor', e);
                  }
                }
              }

              // Trigger background credit refresh on successful write/mutation operations
              // to keep the mini-credit-counter updated reactively.
              const isMutation = ['POST', 'PUT', 'DELETE'].includes(req.method);
              const isBillingRequest = req.url.includes('/billing/');

              if (isMutation && !isBillingRequest) {
                try {
                  const billingService = this.injector.get(BillingUsageService);
                  billingService.refreshAvailableCredits();
                } catch (e) {
                  console.error('Failed to trigger credit refresh from interceptor', e);
                }
              }
            }
          }),
          catchError((error: any) => {
            if (error instanceof HttpErrorResponse) {
              const isNotEnoughCredits =
                (error.status === 400 || error.status === 402) &&
                (error.error?.msg === 'notEnoughCredits' || error.error?.message === 'notEnoughCredits' || error.error?.messageCode === 'notEnoughCredits');

              if (isNotEnoughCredits) {
                try {
                  const billingService = this.injector.get(BillingUsageService);
                  billingService.creditAlertTriggered.set(true);
                } catch (e) {
                  console.error('Failed to set creditAlertTriggered from interceptor', e);
                }
              }

              if (error.status === 403 && error.error?.code === 'auth/email-not-verified') {
                this.authService.isEmailVerified.set(false);
              }

              if (error.status === 403 && error.error?.code === 'ai-restricted') {
                try {
                  const billingService = this.injector.get(BillingUsageService);

                  billingService.aiRestrictedAlertTriggered.set(true);
                } catch (e) {
                  console.error('Failed to set aiRestrictedAlertTriggered from interceptor', e);
                }
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }
}
